// src/components/DriverOutreachModal.js
//
// Reusable driver-outreach log for a transportation request. Shows the
// eligible/available drivers (name · phone · email) and a running log of
// contact attempts (call/text/email + result). Used from both the
// TransportationModal and the Transportation dashboard.
//
// Logging a result of "Accepted" prompts the coordinator to assign that driver
// to the ride right then — assignment stays a separate, confirmed action.
import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import api from "../api";
import { AuthContext } from "../AuthContext";

const METHOD_OPTIONS = [
  { value: "CALL", label: "Call" },
  { value: "TEXT", label: "Text" },
  { value: "EMAIL", label: "Email" },
];

const RESULT_OPTIONS = [
  { value: "NO_ANSWER", label: "No Answer" },
  { value: "VOICEMAIL", label: "Voicemail" },
  { value: "DECLINED", label: "Declined" },
  { value: "ACCEPTED", label: "Accepted" },
];

const METHOD_LABELS = Object.fromEntries(METHOD_OPTIONS.map((o) => [o.value, o.label]));
const RESULT_LABELS = Object.fromEntries(RESULT_OPTIONS.map((o) => [o.value, o.label]));

// Additional driver numbers (driver_phones) are labelled by line type.
const PHONE_TYPE_ICONS = { LANDLINE: "☎️", MOBILE: "📱" };

const RESULT_COLORS = {
  NO_ANSWER: "secondary",
  VOICEMAIL: "info",
  DECLINED: "danger",
  ACCEPTED: "success",
};

function todayLocal() {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

// Format an ISO appointment datetime as "M/D/YYYY h:mm AM/PM" without pulling
// in a date library. Parses the wall-clock parts directly so the time shown
// matches what was entered (no timezone shifting).
function formatAppt(iso) {
  const s = String(iso || "");
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return "";
  const [, y, mo, d, hh, mm] = m;
  let h = Number(hh);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${Number(mo)}/${Number(d)}/${y} ${h}:${mm} ${ampm}`;
}

// The numbers a coordinator could have dialled for a driver: the primary from
// drivers.phone, then each additional driver_phones row. `recorded` is a number
// already logged against an entry — kept in the list even if it has since been
// removed from the driver, so editing an old entry can't silently rewrite which
// number was actually called.
function numberOptions(primary, phones, recorded) {
  const opts = [];
  if (primary) opts.push({ value: primary, label: `📞 ${primary} (primary)` });
  (phones || []).forEach((p) =>
    opts.push({
      value: p.phone_number,
      label:
        `${PHONE_TYPE_ICONS[p.type] || "📞"} ${p.phone_number}` +
        (p.name ? ` — ${p.name}` : ""),
    })
  );
  if (recorded && !opts.some((o) => o.value === recorded)) {
    opts.unshift({ value: recorded, label: `${recorded} (no longer listed)` });
  }
  return opts;
}

const emptyEntry = () => ({
  driver_id: "",
  contacted_number: "",
  contact_method: "CALL",
  result: "NO_ANSWER",
  contacted_on: todayLocal(),
  note: "",
});

export default function DriverOutreachModal({ show, onHide, ride, onAssigned }) {
  const { user } = useContext(AuthContext);

  const [eligible, setEligible] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Add form
  const [form, setForm] = useState(emptyEntry());
  const [saving, setSaving] = useState(false);

  // Inline edit
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyEntry());

  const rideId = ride?.id;

  const canModify = useCallback(
    (entry) => {
      const roles = (user?.roles || []).map((r) => String(r).toUpperCase());
      if (roles.includes("ADMIN") || roles.includes("DIRECTOR")) return true;
      return entry?.created_by != null && Number(entry.created_by) === Number(user?.id);
    },
    [user]
  );

  const loadOutreach = useCallback(async () => {
    if (!rideId) return;
    const res = await api.get(`/drivers/rides/${rideId}/outreach`);
    setEntries(res.data.items || []);
  }, [rideId]);

  // Load eligible drivers + existing log whenever the modal opens.
  useEffect(() => {
    if (!show || !rideId) return;
    setError("");
    setForm(emptyEntry());
    setEditingId(null);

    const load = async () => {
      setLoading(true);
      try {
        const params = { rider_category: ride.rider_category, exclude_ride_id: rideId };
        if (ride.pickup_datetime) params.pickup_datetime = ride.pickup_datetime;
        const [eRes] = await Promise.all([
          api.get("/drivers/eligible", { params }),
          loadOutreach(),
        ]);
        setEligible(eRes.data.items || []);
      } catch (_) {
        setError("Could not load drivers or outreach log.");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, rideId]);

  const driverById = (id) => eligible.find((d) => String(d.id) === String(id));

  // Picking a driver pre-selects their primary number, so an entry logged
  // without touching the Number field still records something sensible.
  const selectDriver = (driverId) => {
    const d = driverById(driverId);
    const opts = numberOptions(d?.phone, d?.phones);
    setForm((p) => ({
      ...p,
      driver_id: driverId,
      contacted_number: opts.length ? opts[0].value : "",
    }));
  };

  // After a successful save, offer to assign the driver if they accepted.
  const maybeAssign = async (driverId, result) => {
    if (result !== "ACCEPTED" || !driverId) return;
    const drv = driverById(driverId);
    const name = drv ? drv.display_name : "this driver";
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Assign ${name} to this ride now?`)) return;
    try {
      await api.post(`/drivers/rides/${rideId}/assign`, { driver_id: Number(driverId) });
      if (onAssigned) onAssigned();
    } catch (e) {
      const msg = e?.response?.data?.error || "Could not assign the driver.";
      setError(msg);
    }
  };

  const handleAdd = async () => {
    if (!form.driver_id) {
      setError("Pick a driver to log.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const res = await api.post(`/drivers/rides/${rideId}/outreach`, {
        driver_id: Number(form.driver_id),
        // An email attempt didn't use a phone number — don't record one.
        contacted_number: form.contact_method === "EMAIL" ? null : form.contacted_number,
        contact_method: form.contact_method,
        result: form.result,
        contacted_on: form.contacted_on || todayLocal(),
        note: form.note,
      });
      setEntries(res.data.items || []);
      const justLogged = { ...form };
      setForm(emptyEntry());
      await maybeAssign(justLogged.driver_id, justLogged.result);
    } catch (e) {
      setError(e?.response?.data?.error || "Could not save the outreach entry.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditForm({
      driver_id: String(entry.driver_id),
      contacted_number: entry.contacted_number || "",
      contact_method: entry.contact_method,
      result: entry.result,
      contacted_on: entry.contacted_on || todayLocal(),
      note: entry.note || "",
    });
  };

  const saveEdit = async (entry) => {
    setError("");
    setSaving(true);
    try {
      const res = await api.put(`/drivers/rides/${rideId}/outreach/${entry.id}`, {
        contacted_number:
          editForm.contact_method === "EMAIL" ? null : editForm.contacted_number,
        contact_method: editForm.contact_method,
        result: editForm.result,
        contacted_on: editForm.contacted_on,
        note: editForm.note,
      });
      setEntries(res.data.items || []);
      setEditingId(null);
      // An edit to "Accepted" is also a cue to assign.
      if (entry.result !== "ACCEPTED") {
        await maybeAssign(String(entry.driver_id), editForm.result);
      }
    } catch (e) {
      setError(e?.response?.data?.error || "Could not update the entry.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm("Delete this outreach entry?")) return;
    setError("");
    try {
      const res = await api.delete(`/drivers/rides/${rideId}/outreach/${entry.id}`);
      setEntries(res.data.items || []);
    } catch (e) {
      setError(e?.response?.data?.error || "Could not delete the entry.");
    }
  };

  const selectedDriver = driverById(form.driver_id);

  const apptWhen = formatAppt(ride?.pickup_datetime);
  const providerName = ride?.provider?.name || "";
  const dropoffLocation = ride?.dropoff_location_text || "";

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <div>
          <Modal.Title className="h5 mb-1">Driver Outreach</Modal.Title>
          {/* Client contact info — same style as the service modals. */}
          <div className="small text-muted d-flex flex-wrap align-items-center gap-3">
            <span className="fw-semibold text-body">{ride?.client_name || "—"}</span>
            {ride?.client_phone ? (
              <span><i className="bi bi-telephone me-1" />{ride.client_phone}</span>
            ) : null}
            {ride?.client_address ? (
              <span><i className="bi bi-geo-alt me-1" />{ride.client_address}</span>
            ) : null}
          </div>
          {(apptWhen || providerName || dropoffLocation) && (
            <div className="small text-muted mt-1">
              <i className="bi bi-calendar-event me-1" />
              Appointment: {apptWhen || "—"}
              {providerName ? ` — ${providerName}` : ""}
              {dropoffLocation ? ` — ${dropoffLocation}` : ""}
            </div>
          )}
        </div>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        {/* Log a new contact attempt */}
        <div className="border rounded p-3 mb-3 bg-light">
          <div className="fw-semibold mb-2">Log a contact attempt</div>
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <Form.Label className="mb-1 small">Driver</Form.Label>
              <Form.Select
                value={form.driver_id}
                onChange={(e) => selectDriver(e.target.value)}
                disabled={loading}
              >
                <option value="">{loading ? "Loading…" : "— Select driver —"}</option>
                {eligible.map((d) => {
                  // A <option> can't wrap or hold markup, so the extra numbers
                  // ride inline after the primary: "555-1234 📱 555-5555 (Dave)".
                  const extras = (d.phones || []).map(
                    (p) =>
                      `${PHONE_TYPE_ICONS[p.type] || "📞"} ${p.phone_number}` +
                      (p.name ? ` (${p.name})` : "")
                  );
                  const contact = [d.phone, ...extras, d.email]
                    .filter(Boolean)
                    .join(", ");
                  return (
                    <option key={d.id} value={String(d.id)}>
                      {d.display_name} ({d.driver_type})
                      {contact ? ` — ${contact}` : ""}
                    </option>
                  );
                })}
              </Form.Select>
              {!loading && eligible.length === 0 && (
                <Form.Text className="text-muted">
                  No eligible &amp; available drivers for this ride.
                </Form.Text>
              )}
            </div>
            <div className="col-md-3">
              <Form.Label className="mb-1 small">Method</Form.Label>
              <Form.Select
                value={form.contact_method}
                onChange={(e) => setForm((p) => ({ ...p, contact_method: e.target.value }))}
              >
                {METHOD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-3">
              <Form.Label className="mb-1 small">Result</Form.Label>
              <Form.Select
                value={form.result}
                onChange={(e) => setForm((p) => ({ ...p, result: e.target.value }))}
              >
                {RESULT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-2">
              <Form.Label className="mb-1 small">Date</Form.Label>
              <Form.Control
                type="date"
                value={form.contacted_on}
                onChange={(e) => setForm((p) => ({ ...p, contacted_on: e.target.value }))}
              />
            </div>
          </div>

          {/* Contact details for the chosen driver */}
          {selectedDriver && (
            <div className="small text-muted mt-2">
              <span className="me-3">📞 {selectedDriver.phone || "—"}</span>
              <span>✉️ {selectedDriver.email || "—"}</span>
              {(selectedDriver.phones || []).map((p) => (
                <div key={p.id} className="mt-1">
                  {PHONE_TYPE_ICONS[p.type] || "📞"} {p.phone_number}
                  {p.name ? ` — ${p.name}` : ""}
                </div>
              ))}
            </div>
          )}

          <div className="row g-2 mt-1 align-items-end">
            <div className="col-md-4">
              <Form.Label className="mb-1 small">
                {form.contact_method === "EMAIL" ? "Email used" : "Number called/texted"}
              </Form.Label>
              {form.contact_method === "EMAIL" ? (
                <Form.Control
                  plaintext
                  readOnly
                  className="ps-2"
                  value={selectedDriver?.email || "— no email on file —"}
                />
              ) : (
                <Form.Select
                  value={form.contacted_number}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, contacted_number: e.target.value }))
                  }
                  disabled={!form.driver_id}
                >
                  {!form.driver_id && <option value="">— Select a driver first —</option>}
                  {numberOptions(selectedDriver?.phone, selectedDriver?.phones).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                  {form.driver_id &&
                    numberOptions(selectedDriver?.phone, selectedDriver?.phones).length === 0 && (
                      <option value="">— no number on file —</option>
                    )}
                </Form.Select>
              )}
            </div>
            <div className="col">
              <Form.Label className="mb-1 small">Note (optional)</Form.Label>
              <Form.Control
                value={form.note}
                onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
                placeholder="e.g. left voicemail, will call back"
              />
            </div>
            <div className="col-auto">
              <Button onClick={handleAdd} disabled={saving || loading || !form.driver_id}>
                {saving && <Spinner size="sm" animation="border" className="me-1" />}
                Add Entry
              </Button>
            </div>
          </div>
        </div>

        {/* Existing log */}
        {loading ? (
          <div className="text-center text-muted py-3">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading…
          </div>
        ) : entries.length === 0 ? (
          <div className="text-muted text-center py-3">No outreach logged yet.</div>
        ) : (
          <Table size="sm" responsive className="align-middle">
            <thead>
              <tr>
                <th>Driver</th>
                <th>Number Used</th>
                <th>Email</th>
                <th>Method</th>
                <th>Result</th>
                <th>Date</th>
                <th>By</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) =>
                editingId === entry.id ? (
                  <tr key={entry.id}>
                    <td>{entry.driver_name}</td>
                    <td>
                      {editForm.contact_method === "EMAIL" ? (
                        <span className="text-muted">—</span>
                      ) : (
                        <Form.Select
                          size="sm"
                          value={editForm.contacted_number}
                          onChange={(e) =>
                            setEditForm((p) => ({ ...p, contacted_number: e.target.value }))
                          }
                        >
                          <option value="">— not recorded —</option>
                          {numberOptions(
                            entry.phone,
                            entry.phones,
                            entry.contacted_number
                          ).map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Form.Select>
                      )}
                    </td>
                    <td>{entry.email || "—"}</td>
                    <td>
                      <Form.Select
                        size="sm"
                        value={editForm.contact_method}
                        onChange={(e) => setEditForm((p) => ({ ...p, contact_method: e.target.value }))}
                      >
                        {METHOD_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Select
                        size="sm"
                        value={editForm.result}
                        onChange={(e) => setEditForm((p) => ({ ...p, result: e.target.value }))}
                      >
                        {RESULT_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="date"
                        value={editForm.contacted_on}
                        onChange={(e) => setEditForm((p) => ({ ...p, contacted_on: e.target.value }))}
                      />
                    </td>
                    <td colSpan={2} className="text-nowrap">
                      <Button size="sm" variant="success" className="me-1" onClick={() => saveEdit(entry)} disabled={saving}>
                        Save
                      </Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                    </td>
                  </tr>
                ) : (
                  <tr key={entry.id}>
                    <td>{entry.driver_name}</td>
                    <td className="text-nowrap">
                      {entry.contacted_number || <span className="text-muted">—</span>}
                    </td>
                    <td>{entry.email || "—"}</td>
                    <td>{METHOD_LABELS[entry.contact_method] || entry.contact_method}</td>
                    <td>
                      <Badge bg={RESULT_COLORS[entry.result] || "secondary"}>
                        {RESULT_LABELS[entry.result] || entry.result}
                      </Badge>
                    </td>
                    <td className="text-nowrap">{entry.contacted_on}</td>
                    <td className="text-nowrap">{entry.created_by_name || "—"}</td>
                    <td className="text-nowrap text-end">
                      {canModify(entry) && (
                        <>
                          <Button size="sm" variant="link" className="p-0 me-2" onClick={() => startEdit(entry)}>
                            Edit
                          </Button>
                          <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => handleDelete(entry)}>
                            Delete
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </Table>
        )}
        {entries.some((e) => e.note) && (
          <div className="small text-muted mt-2">
            {entries
              .filter((e) => e.note)
              .map((e) => (
                <div key={e.id}>
                  <strong>{e.driver_name}:</strong> {e.note}
                </div>
              ))}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
